
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { getProcessDetailById, getUserList, saveProcess } from '@/apis/process/process'
import {
  ApproverOptionsModel,
  ApproverUserListParamModel,
  ApproverUserModel, ProcessApprover,
  ProcessDetailModel, ProcessUpdateParamModel
} from '@/model/process/processModel'
import Container from '@/components/form/container/Container.vue'
import { ElLoading, ElMessage, ElSelect } from 'element-plus'

export default defineComponent({
  props: {
    labelWidth: {
      type: String,
      default: '480px'
    }
  },
  components: {
    Container
  },
  setup() {
    const { t } = useI18n()
    const id = useRoute().params.id.toString()
    const selectIt = ref(ElSelect)
    const dataMap = reactive({
      detailData: {} as ProcessDetailModel | null,
      listLoading: true,
      uiIsActive: true,
      showProcessLevel: 0,
      activeOptions: [
        { label: t('process.yes'), value: true },
        { label: t('process.no'), value: false }
      ],
      itemsTest: ref([{ id: 1, message: 'Foo' }, { id: 2, message: 'Bar' }]),
      level1SelectIndex: -1,
      level2SelectIndex: -1,
      level3SelectIndex: -1,
      dialogApproverSelectUserId: null,
      dialogShowApprover: false,
      dialogLevelType: 0,
      approverOptions: Array<ApproverOptionsModel>(),
      users: Array<ApproverUserModel>(),
      async getDetail() {
        dataMap.listLoading = true
        const data = await getProcessDetailById(id)
        if (data && data.data) {
          dataMap.detailData = data.data
          dataMap.uiIsActive = dataMap.detailData?.active || false
          if (dataMap.detailData && dataMap.detailData?.levelCount) {
            dataMap.showProcessLevel = dataMap.detailData?.levelCount
          }
        }
        const params: ApproverUserListParamModel = {
          userName: '',
          userRoleNames: '',
          userAccount: '',
          active: true
        }
        const userData = await getUserList(params)
        if (userData && userData.data && userData.data.length > 0) {
          dataMap.users = userData.data
          dataMap.approverOptions = userData.data.map(
            (item) => ({
              value: item.userId,
              label: item.userName + ', ' + item.userAccount + ', ' + item.mailAddress
            })
          )
        }
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      selectLevel1Item(index: number) {
        dataMap.level1SelectIndex = index
      },
      selectLevel2Item(index: number) {
        dataMap.level2SelectIndex = index
      },
      selectLevel3Item(index: number) {
        dataMap.level3SelectIndex = index
      },
      showDialogLevel1Approver() {
        dataMap.dialogApproverSelectUserId = null
        dataMap.dialogShowApprover = true
        dataMap.dialogLevelType = 1
      },
      showDialogLevel2Approver() {
        dataMap.dialogApproverSelectUserId = null
        dataMap.dialogShowApprover = true
        dataMap.dialogLevelType = 2
      },
      showDialogLevel3Approver() {
        dataMap.dialogApproverSelectUserId = null
        dataMap.dialogShowApprover = true
        dataMap.dialogLevelType = 3
      },
      levelApproverSelectConfirm(levelType: number) {
        switch (levelType) {
          case 1: { // Select level1 Approver id
            const foundItem = dataMap.detailData?.level1.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
            if (foundItem !== null && typeof (foundItem) !== 'undefined') {
              ElMessage({
                message: t('process.hasApprover'),
                type: 'success',
                duration: 2000
              })
            } else {
              const user = dataMap.users.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
              const approver: ProcessApprover = {
                id: null,
                processId: dataMap.detailData?.level1[0]?.processId as number,
                level: 1,
                userId: user?.userId as number,
                userName: user?.userName as string,
                userRoleNames: user?.userRoleNames as string,
                userAccount: user?.userAccount as string,
                mailAddress: user?.mailAddress as string
              }
              // eslint-disable-next-line no-unused-expressions
              dataMap.detailData?.level1.push(approver)
            }
            break
          }
          case 2: { // Select level2 Approver id
            const foundItem = dataMap.detailData?.level2.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
            if (foundItem !== null && typeof (foundItem) !== 'undefined') {
              ElMessage({
                message: t('process.hasApprover'),
                type: 'success',
                duration: 2000
              })
            } else {
              const user = dataMap.users.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
              const approver: ProcessApprover = {
                id: null,
                processId: dataMap.detailData?.level2[0]?.processId as number,
                level: 1,
                userId: user?.userId as number,
                userName: user?.userName as string,
                userRoleNames: user?.userRoleNames as string,
                userAccount: user?.userAccount as string,
                mailAddress: user?.mailAddress as string
              }
              // eslint-disable-next-line no-unused-expressions
              dataMap.detailData?.level2.push(approver)
            }
            break
          }
          case 3: { // Select level3 Approver id
            const foundItem = dataMap.detailData?.level3.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
            if (foundItem !== null && typeof (foundItem) !== 'undefined') {
              ElMessage({
                message: t('process.hasApprover'),
                type: 'success',
                duration: 2000
              })
            } else {
              const user = dataMap.users.find(obj => obj.userId === dataMap.dialogApproverSelectUserId)
              const approver: ProcessApprover = {
                id: null,
                processId: dataMap.detailData?.level3[0]?.processId as number,
                level: 1,
                userId: user?.userId as number,
                userName: user?.userName as string,
                userRoleNames: user?.userRoleNames as string,
                userAccount: user?.userAccount as string,
                mailAddress: user?.mailAddress as string
              }
              // eslint-disable-next-line no-unused-expressions
              dataMap.detailData?.level3.push(approver)
            }
            break
          }
          default:
            console.log('超出范围')
        }
        dataMap.dialogShowApprover = false
      },
      deleteSelectedLevel1Item() {
        if (dataMap.level1SelectIndex !== -1) {
          if (dataMap.detailData !== null) {
            dataMap.detailData.level1.splice(dataMap.level1SelectIndex, 1)
            dataMap.level1SelectIndex = -1 // 重置选中项索引
          }
        }
      },
      deleteSelectedLevel2Item() {
        if (dataMap.level2SelectIndex !== -1) {
          if (dataMap.detailData !== null) {
            dataMap.detailData.level2.splice(dataMap.level2SelectIndex, 1)
            dataMap.level2SelectIndex = -1 // 重置选中项索引
          }
        }
      },
      deleteSelectedLevel3Item() {
        if (dataMap.level3SelectIndex !== -1) {
          if (dataMap.detailData !== null) {
            dataMap.detailData.level3.splice(dataMap.level3SelectIndex, 1)
            dataMap.level3SelectIndex = -1 // 重置选中项索引
          }
        }
      },
      async onSaveApproverUpdate() {
        if (dataMap.detailData !== null) {
          if (dataMap.detailData.levelCount !== null) {
            switch (dataMap.detailData.levelCount) {
              case 1:
                // 承兑票审批流
                if (dataMap.detailData.level1.length < 1) {
                  ElMessage({
                    message: t('process.ApproverEmpty'),
                    type: 'error',
                    duration: 2000
                  })
                  return
                }
                break
              case 3:
                // 审批流
                if (dataMap.detailData.level1.length < 1 ||
                  dataMap.detailData.level2.length < 1 ||
                  dataMap.detailData.level3.length < 1) {
                  ElMessage({
                    message: t('process.ApproverEmpty'),
                    type: 'error',
                    duration: 2000
                  })
                  return
                }
                break
              default:
                break
            }
          }
          const loading = ElLoading.service({
            lock: true,
            text: 'Loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })
          const processParam: ProcessUpdateParamModel = {
            id: dataMap.detailData.id,
            levelCount: dataMap.detailData.levelCount,
            level1: dataMap.detailData.level1,
            level2: dataMap.detailData.level2,
            level3: dataMap.detailData.level3
          }
          const result = await saveProcess(processParam)
          if (result?.data && result?.data > 0) {
            ElMessage({
              message: t('common.updateSuccess'),
              type: 'success',
              duration: 2000
            })
          } else {
            ElMessage({
              message: result?.msg,
              type: 'error',
              duration: 2000
            })
          }
          loading.close()
        }
      }
    })
    onMounted(() => {
      dataMap.getDetail()
    })
    return {
      t,
      selectIt,
      ...toRefs(dataMap)
    }
  }
})
